import { z } from "zod";
import {
  ALL_PERMISSIONS, AUDIT_RESULTS, ACTOR_TYPES, CUSTOMER_STATUSES, JOB_STATUSES, JOB_TYPES, sortRuntimeVersions, USER_KINDS,
  type Permission,
} from "@hostdock/types";

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 256;

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .pipe(z.email({ message: "Enter a valid email address" }));

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(PASSWORD_MAX_LENGTH, `Password must be at most ${PASSWORD_MAX_LENGTH} characters`);

export const nameSchema = z.string().trim().min(1, "Required").max(120);
export const uuidSchema = z.uuid();
export const totpCodeSchema = z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code");
export const recoveryCodeSchema = z.string().trim().regex(/^[A-Za-z0-9]{5}-?[A-Za-z0-9]{5}$/, "Invalid recovery code");

export const loginSchema = z.object({
  email: emailSchema,
  // Login accepts any non-empty password so old/short passwords still produce a uniform error.
  password: z.string().min(1, "Required").max(PASSWORD_MAX_LENGTH),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const mfaLoginSchema = z.object({
  challengeId: z.string().min(16).max(128),
  code: z.union([totpCodeSchema, recoveryCodeSchema]),
});
export type MfaLoginInput = z.infer<typeof mfaLoginSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Required").max(PASSWORD_MAX_LENGTH),
  newPassword: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const mfaConfirmSchema = z.object({ code: totpCodeSchema });
export type MfaConfirmInput = z.infer<typeof mfaConfirmSchema>;

export const mfaDisableSchema = z.object({
  password: z.string().min(1, "Required").max(PASSWORD_MAX_LENGTH),
  code: z.union([totpCodeSchema, recoveryCodeSchema]),
});
export type MfaDisableInput = z.infer<typeof mfaDisableSchema>;

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

const sortDir = z.enum(["asc", "desc"]).default("desc");

const permissionSchema = z.enum(ALL_PERMISSIONS as [Permission, ...Permission[]]);

// ---------- Admins ----------
export const createAdminSchema = z.object({
  email: emailSchema,
  name: nameSchema,
  roleId: uuidSchema,
});
export type CreateAdminInput = z.infer<typeof createAdminSchema>;

export const updateAdminSchema = z
  .object({ name: nameSchema.optional(), roleId: uuidSchema.optional() })
  .refine((v) => v.name !== undefined || v.roleId !== undefined, "Nothing to update");
export type UpdateAdminInput = z.infer<typeof updateAdminSchema>;

export const listAdminsQuery = paginationSchema.extend({
  search: z.string().trim().max(200).optional(),
  status: z.enum(["ACTIVE", "DISABLED"]).optional(),
});

// ---------- Roles ----------
export const roleBodySchema = z.object({
  name: z.string().trim().min(2).max(60),
  description: z.string().trim().max(300).default(""),
  permissions: z.array(permissionSchema).max(ALL_PERMISSIONS.length).transform((p) => [...new Set(p)]),
});
export type RoleInput = z.input<typeof roleBodySchema>;

// ---------- Customers ----------
export const createCustomerSchema = z.object({
  email: emailSchema,
  name: nameSchema,
  displayName: nameSchema,
  companyName: z.string().trim().max(160).optional().nullable(),
  phone: z.string().trim().max(40).optional().nullable(),
});
export type CreateCustomerInput = z.infer<typeof createCustomerSchema>;

export const updateCustomerSchema = z.object({
  displayName: nameSchema.optional(),
  companyName: z.string().trim().max(160).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  ownerName: nameSchema.optional(),
});
export type UpdateCustomerInput = z.infer<typeof updateCustomerSchema>;

export const customerStatusChangeSchema = z.object({
  status: z.enum(CUSTOMER_STATUSES),
  reason: z.string().trim().min(3, "Give a short reason").max(500),
});
export type CustomerStatusChangeInput = z.infer<typeof customerStatusChangeSchema>;

export const listCustomersQuery = paginationSchema.extend({
  search: z.string().trim().max(200).optional(),
  status: z.enum(CUSTOMER_STATUSES).optional(),
  sort: z.enum(["createdAt", "displayName"]).default("createdAt"),
  dir: sortDir,
});

export const updateProfileSchema = z.object({
  name: nameSchema.optional(),
  displayName: nameSchema.optional(),
  companyName: z.string().trim().max(160).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

// ---------- Sessions ----------
export const listSessionsQuery = paginationSchema.extend({
  kind: z.enum(USER_KINDS).optional(),
  userId: uuidSchema.optional(),
});

// ---------- Audit ----------
export const listAuditQuery = paginationSchema.extend({
  search: z.string().trim().max(200).optional(),
  action: z.string().trim().max(80).optional(),
  actorType: z.enum(ACTOR_TYPES).optional(),
  actorId: uuidSchema.optional(),
  targetType: z.string().trim().max(60).optional(),
  targetId: z.string().trim().max(120).optional(),
  customerId: uuidSchema.optional(),
  result: z.enum(AUDIT_RESULTS).optional(),
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
});

// ---------- Jobs ----------
export const listJobsQuery = paginationSchema.extend({
  status: z.enum(JOB_STATUSES).optional(),
  type: z.enum(JOB_TYPES).optional(),
});

// ---------- Settings ----------
export const securityPolicySchema = z.object({
  adminMfaRequired: z.boolean(),
  customerMfaRequired: z.boolean(),
  sessionIdleMinutes: z.number().int().min(5).max(24 * 60),
  sessionAbsoluteHours: z.number().int().min(1).max(24 * 30),
  loginMaxAttempts: z.number().int().min(3).max(50),
  lockoutMinutes: z.number().int().min(1).max(24 * 60),
});


export const reasonSchema = z.object({ reason: z.string().trim().min(3, "Give a short reason").max(500) });

// ---------- Infrastructure ----------
export const regionCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9][A-Z0-9-]{0,15}$/, "Use 1-16 letters, digits or dashes (e.g. BD, US-EAST)");

export const regionBodySchema = z.object({
  code: regionCodeSchema,
  name: z.string().trim().min(2).max(80),
  location: z.string().trim().max(120).default(""),
  status: z.enum(["ACTIVE", "DISABLED"]).default("ACTIVE"),
});
export type RegionInput = z.input<typeof regionBodySchema>;

export const ipSchema = z.union([z.ipv4(), z.ipv6()], { message: "Enter a valid IPv4 or IPv6 address" });
export const hostnameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(253)
  .regex(/^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/, "Enter a valid hostname");
export const serverNameSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9][A-Z0-9-]{1,39}$/, "Use 2-40 letters, digits or dashes (e.g. BD-NODE-01)");
export const sshUserSchema = z.string().trim().regex(/^[a-z_][a-z0-9_-]{0,31}$/, "Enter a valid Linux user name");
export const sshPrivateKeySchema = z
  .string()
  .max(16_384)
  .refine((v) => /-----BEGIN [A-Z0-9 ]*PRIVATE KEY-----[\s\S]+-----END [A-Z0-9 ]*PRIVATE KEY-----/.test(v.trim()), "Paste a private key in OpenSSH or PEM format");
export const labelsSchema = z
  .record(z.string().regex(/^[a-z0-9][a-z0-9._/-]{0,62}$/, "Invalid label key"), z.string().max(120))
  .refine((l) => Object.keys(l).length <= 20, "At most 20 labels")
  .default({});

export const sshAuthSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("KEY"), privateKey: sshPrivateKeySchema, passphrase: z.string().max(256).optional() }),
  z.object({ type: z.literal("PASSWORD"), password: z.string().min(1).max(256) }),
]);
export type SshAuthInput = z.infer<typeof sshAuthSchema>;

export const connectionTargetSchema = z.object({
  publicIp: ipSchema,
  sshPort: z.number().int().min(1).max(65535).default(22),
  sshUser: sshUserSchema,
  auth: sshAuthSchema,
});
export type ConnectionTargetInput = z.input<typeof connectionTargetSchema>;

export const createServerSchema = connectionTargetSchema.extend({
  name: serverNameSchema,
  hostname: hostnameSchema,
  regionId: uuidSchema,
  privateIp: ipSchema.nullable().optional(),
  labels: labelsSchema,
  /** SHA256 host key fingerprint confirmed by "Test connection" (trust on first use). */
  hostKeyFingerprint: z.string().regex(/^SHA256:[A-Za-z0-9+/]{43}$/, "Run Test Connection first"),
  autoActivate: z.boolean().default(true),
  mongodbEnabled: z.boolean().default(false),
  /** AUTO: direct if the server can reach the gateway, otherwise an SSH tunnel run by the control plane. */
  agentConnectivity: z.enum(["AUTO", "DIRECT", "TUNNEL"]).default("AUTO"),
});
export type CreateServerInput = z.input<typeof createServerSchema>;

export const updateServerSchema = z
  .object({
    hostname: hostnameSchema.optional(),
    privateIp: ipSchema.nullable().optional(),
    labels: labelsSchema.optional(),
    agentConnectivity: z.enum(["AUTO", "DIRECT", "TUNNEL"]).optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), "Nothing to update");
export type UpdateServerInput = z.infer<typeof updateServerSchema>;

export const listServersQuery = paginationSchema.extend({
  search: z.string().trim().max(200).optional(),
  regionId: uuidSchema.optional(),
  status: z.string().trim().max(20).optional(),
});

export const metricsQuery = z.object({ range: z.enum(["1h", "6h", "24h", "7d", "30d"]).default("1h") });

export const agentCommandSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("READINESS") }),
  z.object({ type: z.literal("REBOOT"), reason: z.string().trim().min(3).max(500) }),
  z.object({
    type: z.literal("LOGS"),
    source: z.enum(["agent", "docker", "system", "sshd", "proxy"]),
    lines: z.number().int().min(10).max(2000).default(200),
    since: z.enum(["15m", "1h", "6h", "24h"]).default("1h"),
    grep: z.string().trim().max(120).optional(),
  }),
]);
export type AgentCommandInput = z.input<typeof agentCommandSchema>;

// Saved oldest first (the newest is the default for new websites), whatever order was typed.
export const runtimeCatalogueSchema = z.object({
  php: z.array(z.string().regex(/^\d+\.\d+$/)).min(1).max(16).transform(sortRuntimeVersions),
  node: z.array(z.string().regex(/^\d+$/)).min(1).max(16).transform(sortRuntimeVersions),
  python: z.array(z.string().regex(/^\d+\.\d+$/)).min(1).max(16).transform(sortRuntimeVersions),
});

// ---------- Hosting ----------
const int = (min: number, max: number) => z.number().int().min(min).max(max);

export const planLimitsSchema = z.object({
  websites: int(1, 10_000),
  cpuCores: z.number().min(0.1).max(64),
  memoryMb: int(128, 262_144),
  processes: int(32, 100_000).default(512),
  ioMbps: int(0, 100_000).default(0),
  storageGb: int(1, 100_000),
  inodes: int(10_000, 100_000_000),
  bandwidthGb: int(0, 1_000_000),
  databases: int(0, 10_000),
  databaseStorageGb: int(0, 100_000),
  nodeApps: int(0, 10_000),
  pythonApps: int(0, 10_000),
  cronJobs: int(0, 10_000),
  backupRetentionDays: int(0, 3650),
  sftpUsers: int(0, 1000),
  mailboxes: int(0, 100_000).default(0),
  mailboxQuotaMb: int(50, 102_400).default(1024),
});

const versionList = (re: RegExp) => z.array(z.string().regex(re)).max(10);

export const planBodySchema = z.object({
  name: z.string().trim().min(2).max(60),
  slug: z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9-]{1,40}$/, "Use lowercase letters, digits and dashes"),
  description: z.string().trim().max(500).default(""),
  status: z.enum(["ACTIVE", "ARCHIVED"]).default("ACTIVE"),
  limits: planLimitsSchema,
  features: z.object({ redis: z.boolean(), mongodb: z.boolean(), git: z.boolean() }),
  runtimes: z.object({ php: versionList(/^\d+\.\d+$/), node: versionList(/^\d+$/), python: versionList(/^\d+\.\d+$/) }),
  regionIds: z.array(uuidSchema).max(100).default([]),
  placementLabels: z.record(z.string().regex(/^[a-z0-9][a-z0-9._/-]{0,62}$/), z.string().max(120)).default({}),
});
export type PlanInput = z.input<typeof planBodySchema>;

/** Region/server are set by staff at order time; omit both to let HostDock place the package automatically. */
export const createSubscriptionSchema = z.object({
  planId: uuidSchema,
  externalRef: z.string().trim().max(120).nullable().optional(),
  regionId: uuidSchema.nullable().optional(),
  serverId: uuidSchema.nullable().optional(),
});
export const updateSubscriptionSchema = z.object({ status: z.enum(["ACTIVE", "SUSPENDED", "CANCELLED"]).optional(), planId: uuidSchema.optional() });

/** A registrable hostname: at least two labels, lowercase, no trailing dot. */
export const domainNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .transform((v) => v.replace(/\.$/, ""))
  .pipe(z.string().max(253).regex(/^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,62}$/, "Enter a valid domain name, e.g. example.com"));

export const phpSettingsSchema = z.object({
  memoryLimitMb: int(32, 4096),
  uploadMaxMb: int(1, 2048),
  postMaxMb: int(1, 2048),
  maxExecutionTime: int(1, 3600),
  maxInputVars: int(100, 100_000),
  timezone: z.string().regex(/^[A-Za-z_]+(\/[A-Za-z0-9_+-]+){0,2}$/, "Use an IANA timezone such as Asia/Dhaka"),
  displayErrors: z.boolean(),
  logErrors: z.boolean(),
});

const commandSchema = z.string().max(2000).refine((v) => !v.includes("\0") && !/[\n\r]/.test(v), "Commands must be a single line");
export const nodeSettingsSchema = z.object({ buildCommand: commandSchema, startCommand: commandSchema.min(1), port: int(1024, 65535) });
export const pythonSettingsSchema = z.object({
  preset: z.enum(["DJANGO", "FLASK", "FASTAPI", "CUSTOM"]),
  requirementsFile: z.string().regex(/^[A-Za-z0-9._/-]{1,120}$/),
  startCommand: commandSchema.min(1),
  port: int(1024, 65535),
});
export const envVarsSchema = z
  .record(z.string().regex(/^[A-Z_][A-Z0-9_]{0,63}$/, "Use UPPER_SNAKE_CASE names"), z.string().max(8192))
  .refine((e) => Object.keys(e).length <= 100, "At most 100 variables")
  .refine((e) => !Object.keys(e).some((k) => ["PORT", "HOME", "PATH"].includes(k)), "PORT, HOME and PATH are managed by HostDock");

export const wordpressUsernameSchema = z.string().trim().min(3, "At least 3 characters").max(60).regex(/^[a-z0-9._-]+$/, "Use lowercase letters, digits, dot, dash or underscore").refine((u) => u !== "admin", "Choose a username other than 'admin'");
export const wordpressInstallSchema = z.object({
  title: z.string().trim().min(1, "Enter a site title").max(120),
  adminUsername: wordpressUsernameSchema,
  adminEmail: z.email("Enter a valid email").max(254),
  locale: z.string().regex(/^[a-z]{2,3}(_[A-Z]{2})?$/, "Invalid locale").default("en_US"),
});

export const createWebsiteSchema = z.object({
  subscriptionId: uuidSchema,
  /** Staff only. Defaults to the subscription's region; customers cannot choose one. */
  regionId: uuidSchema.optional(),
  appType: z.enum(["PHP", "WORDPRESS", "WOOCOMMERCE", "NODE", "PYTHON", "STATIC"]),
  /** Required for WordPress and WooCommerce. */
  wordpress: wordpressInstallSchema.optional(),
  domain: domainNameSchema,
  /** The runtime version (e.g. "8.4", "24"). Empty for a STATIC site, which has no application runtime. */
  runtimeVersion: z.string().regex(/^\d+(\.\d+)?$/).or(z.literal("")).optional(),
  database: z.object({ engine: z.enum(["MARIADB", "POSTGRESQL", "MONGODB"]) }).nullable().optional(),
  redis: z.boolean().default(false),
  wwwAlias: z.boolean().default(true),
  /** Manual placement; requires SERVER_MANAGE. Defaults to the subscription's server. */
  serverId: uuidSchema.nullable().optional(),
}).refine((w) => !["WORDPRESS", "WOOCOMMERCE"].includes(w.appType) || Boolean(w.wordpress), { message: "WordPress settings are required", path: ["wordpress"] })
  .refine((w) => !["WORDPRESS", "WOOCOMMERCE"].includes(w.appType) || !w.database || w.database.engine === "MARIADB", { message: "WordPress uses MariaDB", path: ["database"] })
  // A static site has no runtime version and no database; every other type needs a version.
  .refine((w) => w.appType === "STATIC" || Boolean(w.runtimeVersion), { message: "A runtime version is required", path: ["runtimeVersion"] })
  .refine((w) => w.appType !== "STATIC" || (!w.runtimeVersion && !w.database && !w.redis && !w.wordpress), { message: "A static site has no runtime, database, Redis or WordPress", path: ["appType"] });
export type CreateWebsiteInput = z.input<typeof createWebsiteSchema>;

export const updateWebsiteSettingsSchema = z.object({
  runtimeVersion: z.string().regex(/^\d+(\.\d+)?$/).optional(),
  php: phpSettingsSchema.optional(),
  node: nodeSettingsSchema.optional(),
  python: pythonSettingsSchema.optional(),
  env: envVarsSchema.optional(),
});
export type UpdateWebsiteSettingsInput = z.infer<typeof updateWebsiteSettingsSchema>;

export const addDomainSchema = z
  .object({ name: domainNameSchema, kind: z.enum(["ALIAS", "REDIRECT"]), redirectTo: domainNameSchema.optional(), httpsRedirect: z.boolean().default(true) })
  .refine((d) => d.kind !== "REDIRECT" || Boolean(d.redirectTo), { message: "Redirect target required", path: ["redirectTo"] });
export type AddDomainInput = z.input<typeof addDomainSchema>;

/** A domain added to the customer's list before (or without) a website. Staff name the customer. */
export const newDomainSchema = z.object({ name: domainNameSchema, customerId: uuidSchema.optional() });
export type NewDomainInput = z.input<typeof newDomainSchema>;

export const createDatabaseSchema = z.object({
  websiteId: uuidSchema,
  engine: z.enum(["MARIADB", "POSTGRESQL", "MONGODB"]),
  name: z.string().trim().toLowerCase().regex(/^[a-z][a-z0-9_]{1,20}$/, "Use 2-21 lowercase letters, digits or underscores"),
  privileges: z.enum(["ALL", "READ_ONLY"]).default("ALL"),
});
export type CreateDatabaseInput = z.input<typeof createDatabaseSchema>;

export const listHostingQuery = paginationSchema.extend({
  search: z.string().trim().max(200).optional(),
  status: z.string().trim().max(20).optional(),
  customerId: uuidSchema.optional(),
  serverId: uuidSchema.optional(),
  runtime: z.enum(["php", "node", "python", "apps"]).optional(),
});

export const siteLogsQuery = z.object({
  source: z.enum(["access", "php_error", "app"]),
  since: z.enum(["15m", "1h", "6h", "24h", "7d"]).default("1h"),
  lines: z.coerce.number().int().min(10).max(5000).default(500),
  grep: z.string().trim().max(200).optional(),
});

export const websiteActionSchema = z.object({ action: z.enum(["restart", "stop", "start"]) });

export const updateSettingsSchema = z.object({
  security: securityPolicySchema.partial().optional(),
  runtimes: runtimeCatalogueSchema.optional(),
});
export type UpdateSettingsInput = z.infer<typeof updateSettingsSchema>;

export { z };

// ---------- Customer hosting: files, SFTP, cron, Git ----------

/** A path inside a website's home. Normalised server-side; ".." can never leave the home. */
export const sitePathSchema = z
  .string()
  .max(4096)
  .refine((p) => !p.includes("\0"), "Invalid path")
  .transform((p) => {
    const parts: string[] = [];
    for (const seg of p.split("/")) {
      if (!seg || seg === ".") continue;
      if (seg === "..") parts.pop();
      else parts.push(seg);
    }
    return "/" + parts.join("/");
  });
export const fileNameSchema = z
  .string()
  .trim()
  .min(1, "Enter a name")
  .max(255)
  .refine((n) => !n.includes("/") && !n.includes("\0") && n !== "." && n !== "..", "Names cannot contain '/'");

export const filePathQuery = z.object({ path: sitePathSchema.default("/") });
export const fileWriteSchema = z.object({ path: sitePathSchema, content: z.string().max(2 * 1024 * 1024, "Files larger than 2 MB cannot be edited in the browser") });
export const fileCreateSchema = z.object({ dir: sitePathSchema, name: fileNameSchema, kind: z.enum(["file", "folder"]) });
export const fileRenameSchema = z.object({ from: sitePathSchema, to: sitePathSchema, overwrite: z.boolean().default(false) });
export const fileDeleteSchema = z.object({ paths: z.array(sitePathSchema).min(1).max(1000) });
export const fileChmodSchema = z.object({ path: sitePathSchema, mode: z.string().regex(/^[0-7]{3}$/, "Use three octal digits, e.g. 644") });
export const uploadInitSchema = z.object({
  dir: sitePathSchema,
  name: fileNameSchema,
  size: z.number().int().min(0).max(2 * 1024 ** 3, "Use SFTP for files larger than 2 GB"),
  overwrite: z.boolean().default(false),
});
export const uploadChunkQuery = z.object({ offset: z.coerce.number().int().min(0) });

export const sshPublicKeySchema = z
  .string()
  .trim()
  .max(16 * 1024)
  .regex(/^(ssh-ed25519|ssh-rsa|ecdsa-sha2-nistp(256|384|521)|sk-ssh-ed25519@openssh\.com|sk-ecdsa-sha2-nistp256@openssh\.com) [A-Za-z0-9+/]+=*( [^\r\n]*)?$/, "Paste an OpenSSH public key (ssh-ed25519 AAAA…)");
export const addSshKeySchema = z.object({ name: z.string().trim().min(1, "Name the key").max(80), publicKey: sshPublicKeySchema });

const CRON_FIELD = /^(\*|\d+(-\d+)?)(\/\d+)?(,(\*|\d+(-\d+)?)(\/\d+)?)*$/;
export const cronScheduleSchema = z
  .string()
  .trim()
  .transform((s) => s.replace(/\s+/g, " "))
  .refine((s) => {
    const f = s.split(" ");
    return f.length === 5 && f.every((x) => CRON_FIELD.test(x));
  }, "Use five cron fields: minute hour day-of-month month day-of-week");
export const cronJobSchema = z.object({
  name: z.string().trim().min(1, "Enter a name").max(80),
  schedule: cronScheduleSchema,
  command: z.string().trim().min(1, "Enter a command").max(1000).refine((c) => !/[\r\n\0]/.test(c), "Use a single line"),
  timeoutSeconds: z.number().int().min(10).max(3600).default(300),
  enabled: z.boolean().default(true),
});
// No defaults here: a partial update must only change the fields it names.
export const updateCronJobSchema = z.object({
  name: z.string().trim().min(1, "Enter a name").max(80).optional(),
  schedule: cronScheduleSchema.optional(),
  command: z.string().trim().min(1, "Enter a command").max(1000).refine((c) => !/[\r\n\0]/.test(c), "Use a single line").optional(),
  timeoutSeconds: z.number().int().min(10).max(3600).optional(),
  enabled: z.boolean().optional(),
}).refine((v) => Object.values(v).some((x) => x !== undefined), "Nothing to update");

/** The host a git URL connects to: https://host[:port]/…, [ssh://]user@host[:port][:/]path. Lower-cased, no port. */
export function gitUrlHost(u: string): string {
  let s = u.trim();
  let authority: string;
  if (/^https:\/\//i.test(s)) {
    authority = s.slice(8).split("/")[0] ?? "";
  } else {
    s = s.replace(/^ssh:\/\//i, "");
    const at = s.lastIndexOf("@");
    if (at >= 0) s = s.slice(at + 1);
    authority = s.split(/[:/]/)[0] ?? "";
  }
  const colon = authority.lastIndexOf(":");
  if (colon > 0 && /^\d+$/.test(authority.slice(colon + 1))) authority = authority.slice(0, colon);
  return authority.toLowerCase();
}

/** Reject repository hosts that can only point inward (SSRF): IP literals, localhost, single-label and internal TLDs.
 * The node additionally resolves and re-checks the address (netguard) at fetch time; this is the up-front guard. */
function gitHostAllowed(u: string): boolean {
  const host = gitUrlHost(u);
  if (!host || host.length > 253) return false;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.includes(":") || host.startsWith("[")) return false; // IPv4/IPv6 literal
  if (host === "localhost" || /\.(localhost|local|internal|lan|home\.arpa|intranet|corp)$/.test(host)) return false;
  if (!host.includes(".")) return false; // single-label hostnames are internal-only
  return true;
}

export const gitUrlSchema = z
  .string()
  .trim()
  .max(500)
  .refine(
    (u) => /^(ssh:\/\/)?[A-Za-z0-9._-]+@[A-Za-z0-9.-]+(:\d+)?[:/][A-Za-z0-9._~/-]+(\.git)?$/.test(u) || /^https:\/\/[A-Za-z0-9.-]+(:\d+)?\/[A-Za-z0-9._~/-]+(\.git)?$/.test(u),
    "Use an SSH (git@host:owner/repo.git) or HTTPS repository URL without credentials",
  )
  .refine(gitHostAllowed, "The repository host must be a public server, not an IP address or internal name");
export const gitBranchSchema = z.string().trim().min(1).max(200).regex(/^(?!-)(?!.*\.\.)[A-Za-z0-9._/-]+$/, "Invalid branch name");
export const connectGitSchema = z.object({ url: gitUrlSchema, branch: gitBranchSchema.default("main"), target: z.enum(["app", "public"]) });
export const updateGitSchema = z.object({ url: gitUrlSchema.optional(), branch: gitBranchSchema.optional() });

export type CronJobInput = z.input<typeof cronJobSchema>;
export type ConnectGitInput = z.input<typeof connectGitSchema>;

// ---------- WordPress Manager ----------
export const wordpressSettingsSchema = z.object({
  maintenance: z.boolean().optional(),
  debug: z.boolean().optional(),
  searchEngineVisible: z.boolean().optional(),
  autoUpdates: z.object({ core: z.enum(["off", "minor", "major"]).optional(), plugins: z.boolean().optional(), themes: z.boolean().optional() }).optional(),
});
export const wordpressSlugSchema = z.string().trim().regex(/^[a-z0-9][a-z0-9._-]{0,99}$/, "Use the plugin or theme slug from wordpress.org, e.g. woocommerce");
export const wordpressExtensionActionSchema = z.object({
  action: z.enum(["install", "activate", "deactivate", "update", "delete", "auto-update-on", "auto-update-off"]),
  slug: wordpressSlugSchema,
  activate: z.boolean().default(false),
});
export const wordpressCliSchema = z.object({ command: z.string().trim().min(1, "Enter a command").max(2000) });
export const wordpressUpdateSchema = z.object({ scope: z.enum(["core", "plugins", "themes", "all"]) });
export type WordPressSettingsInput = z.input<typeof wordpressSettingsSchema>;
export type WordPressExtensionActionInput = z.input<typeof wordpressExtensionActionSchema>;

// ---------- Migration Center ----------
const migrationHostSchema = z.string().trim().min(1, "Enter the old server's address").max(300);
export const migrationProviderIdSchema = z.enum(["cpanel", "directadmin", "plesk", "webuzo", "enhance", "cyberpanel", "ispconfig", "ssh", "sftp", "ftp", "wordpress", "wordpress-admin", "archive"]);

export const migrationDetectSchema = z.object({ host: migrationHostSchema, port: z.number().int().min(1).max(65535).nullable().optional() });

export const createMigrationSchema = z.object({
  provider: migrationProviderIdSchema,
  host: migrationHostSchema,
  port: z.number().int().min(1).max(65535).nullable().optional(),
  /** SHA-256 fingerprint of a self-signed certificate the customer reviewed and approved. */
  /** The approved certificate (SHA-256, hex) for panel sources, or the approved SSH host key ("SHA256:…") for SFTP sources. */
  tlsFingerprint: z.string().trim().regex(/^(([0-9A-Fa-f]{2}:){31}[0-9A-Fa-f]{2}|SHA256:[A-Za-z0-9+/]{43})$/, "Invalid fingerprint").nullable().optional(),
  username: z.string().trim().min(1, "Enter the username").max(200),
  secret: z.string().min(1, "Enter the password or token").max(8192),
  extra: z.string().max(8192).optional(),
  subscriptionId: uuidSchema.nullable().optional(),
  /** Which of the source's sign-in methods the credentials are for (e.g. password, token); see the provider's authMethods. */
  authMethod: z.string().regex(/^[a-z_]{1,32}$/).optional(),
});
export type CreateMigrationInput = z.input<typeof createMigrationSchema>;

/** "Test connection" in the wizard: the panel, its login address (port optional, normally detected) and the sign-in. */
export const migrationConnectionTestSchema = z.object({
  provider: migrationProviderIdSchema,
  address: migrationHostSchema,
  port: z.number().int().min(1).max(65535).nullable().optional(),
  tlsFingerprint: z.string().trim().regex(/^([0-9A-Fa-f]{2}:){31}[0-9A-Fa-f]{2}$/, "Invalid fingerprint").nullable().optional(),
  authMethod: z.string().regex(/^[a-z_]{1,32}$/).optional(),
  username: z.string().trim().min(1, "Enter the username").max(200),
  secret: z.string().min(1, "Enter the password").max(8192),
});
export type MigrationConnectionTestInput = z.input<typeof migrationConnectionTestSchema>;

export const migrationSiteChoiceSchema = z.object({
  domain: z.string().trim().toLowerCase().min(1).max(253),
  mode: z.enum(["new", "existing", "alias", "skip"]),
  websiteId: uuidSchema.nullable().optional(),
  aliasOf: z.string().trim().toLowerCase().max(253).nullable().optional(),
});
export const migrationSelectionSchema = z.object({
  resourceIds: z.array(uuidSchema).max(5000),
  /** Single-destination migrations: everything goes into this website. */
  targetWebsiteId: uuidSchema.optional(),
  /** Account migrations: one choice per site of the source account, and the package new websites are created in. */
  account: z.object({
    subscriptionId: uuidSchema,
    sites: z.array(migrationSiteChoiceSchema).min(1).max(200),
    /** Add HostDock's verification TXT record to the old host's DNS zone, so domains verify before DNS is switched. */
    verifyDomains: z.boolean().default(false),
  }).optional(),
  /** merge keeps the website's existing files; replace swaps in the imported site wholesale. */
  filesMode: z.enum(["merge", "replace"]).default("merge"),
}).refine((s) => Boolean(s.targetWebsiteId) !== Boolean(s.account), { message: "Choose a destination website, or a plan for each site", path: ["targetWebsiteId"] });

const sshFingerprint = z.string().regex(/^SHA256:[A-Za-z0-9+/]{43}$/, "Invalid SSH host key fingerprint");
export const migrationTransferAccessSchema = z
  .object({
    protocol: z.enum(["sftp", "ftps", "ftp"]),
    port: z.number().int().min(1).max(65535).nullable().optional(),
    username: z.string().trim().min(1).max(128),
    password: z.string().min(1).max(512).optional(),
    privateKey: z.string().min(40).max(16384).optional(),
    passphrase: z.string().max(512).optional(),
    hostKeyFingerprint: sshFingerprint.optional(),
    allowPlainFtp: z.boolean().optional(),
  })
  .refine((a) => a.protocol !== "sftp" || Boolean(a.password || a.privateKey), { message: "Enter a password or a private key", path: ["password"] })
  .refine((a) => a.protocol === "sftp" || Boolean(a.password), { message: "Enter the password", path: ["password"] })
  .refine((a) => a.protocol !== "ftp" || a.allowPlainFtp === true, { message: "Plain FTP sends your password unencrypted; confirm you accept that or use FTPS/SFTP", path: ["allowPlainFtp"] });

/** Reuse the sign-in the migration was created with (a cPanel password) to copy files and mail. */
export const migrationAccountTransferSchema = z.object({
  useAccountLogin: z.literal(true),
  protocol: z.enum(["sftp", "ftps"]),
  port: z.number().int().min(1).max(65535).nullable().optional(),
  hostKeyFingerprint: sshFingerprint.optional(),
});

export const migrationAccessSchema = z.object({
  /** null removes the transfer sign-in; omit to leave it unchanged. */
  transfer: z.union([migrationAccountTransferSchema, migrationTransferAccessSchema]).nullable().optional(),
  /** Database credentials keyed by the source database name. A null value removes one. */
  databases: z.record(z.string().min(1).max(128), z.object({ username: z.string().trim().min(1).max(128), password: z.string().min(1).max(512) }).nullable()).optional(),
});
export type MigrationAccessInput = z.input<typeof migrationAccessSchema>;

export const migrationHostKeySchema = z.object({ port: z.number().int().min(1).max(65535).default(22) });
export type MigrationSelectionInput = z.input<typeof migrationSelectionSchema>;

// ---------- Email hosting ----------
export const mailLocalPartSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9](?:[a-z0-9._+-]{0,62}[a-z0-9])?$/, "Use letters, digits and . _ + - (not at the start or end)")
  .refine((v) => !v.includes(".."), "No consecutive dots");
export const mailPasswordSchema = z
  .string()
  .min(10, "Use at least 10 characters")
  .max(256)
  .refine((v) => ![...v].some((c) => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127), "No control characters");
export const createMailDomainSchema = z.object({ domain: domainNameSchema });
export const createMailboxSchema = z.object({ localPart: mailLocalPartSchema, password: mailPasswordSchema, quotaMb: z.number().int().min(50).max(102_400).optional() });
export const updateMailboxSchema = z
  .object({ password: mailPasswordSchema.optional(), quotaMb: z.number().int().min(50).max(102_400).optional(), status: z.enum(["ACTIVE", "DISABLED"]).optional() })
  .refine((v) => v.password !== undefined || v.quotaMb !== undefined || v.status !== undefined, "Nothing to change");
export const mailDestinationSchema = z.string().trim().toLowerCase().max(254).email();
export const saveMailAliasSchema = z.object({
  source: z.union([z.literal("*"), mailLocalPartSchema]),
  destinations: z.array(mailDestinationSchema).min(1).max(10),
  enabled: z.boolean().default(true),
});
export type CreateMailDomainInput = z.input<typeof createMailDomainSchema>;
export type CreateMailboxInput = z.input<typeof createMailboxSchema>;
export type UpdateMailboxInput = z.input<typeof updateMailboxSchema>;
export type SaveMailAliasInput = z.input<typeof saveMailAliasSchema>;

// ---------- API tokens and webhooks ----------
const apiTokenScopeEnum = z.enum(["websites:read", "websites:write", "databases:read", "databases:write", "domains:read", "mail:read", "mail:write", "migrations:read", "migrations:write"]);
export const createApiTokenSchema = z.object({
  name: z.string().trim().min(1).max(80),
  scopes: z.array(apiTokenScopeEnum).min(1).max(9).transform((s) => [...new Set(s)]),
  expiresInDays: z.number().int().min(1).max(365).default(90),
});
export type CreateApiTokenInput = z.input<typeof createApiTokenSchema>;

const webhookEvent = z.enum(["*", "website.created", "website.failed", "migration.completed", "migration.completed_with_warnings", "migration.failed", "migration.cancelled", "mailbox.created", "mailbox.deleted", "backup.completed", "backup.failed", "webhook.test"]);
export const webhookEndpointSchema = z.object({
  url: z.string().trim().max(2000).url().refine((u) => u.startsWith("https://"), "Webhook URLs must use https://"),
  description: z.string().trim().max(200).default(""),
  events: z.array(webhookEvent).min(1).max(12).transform((e) => [...new Set(e)]),
});
export const updateWebhookEndpointSchema = webhookEndpointSchema.partial().extend({ enabled: z.boolean().optional() });
export type WebhookEndpointInput = z.input<typeof webhookEndpointSchema>;
export type UpdateWebhookEndpointInput = z.input<typeof updateWebhookEndpointSchema>;

// ---------- DNS ----------
const dnsRecordType = z.enum(["A", "AAAA", "CNAME", "MX", "TXT", "SRV", "CAA", "NS"]);
/** A record name: the apex ("@" or the domain), a relative label, or an FQDN. Validated against the zone server-side. */
const dnsName = z.string().trim().toLowerCase().min(1).max(253).regex(/^(@|\*|(\*\.)?([a-z0-9_]([a-z0-9_-]{0,61}[a-z0-9_])?\.)*[a-z0-9_]([a-z0-9_-]{0,61}[a-z0-9_])?\.?)$/, "Enter a valid record name");
const dnsTtl = z.union([z.literal(1), z.number().int().min(60).max(604800)]).default(1); // 1 = provider "automatic"
export const connectDnsZoneSchema = z
  .object({
    domain: domainNameSchema,
    provider: z.enum(["cloudflare", "hostdock"]),
    /** The provider's API token; HostDock DNS needs none. */
    credential: z.string().trim().min(1).max(512).optional(),
  })
  .refine((v) => v.provider === "hostdock" || !!v.credential, { message: "Enter the API token", path: ["credential"] });
const cloudnsAuthType = z.enum(["auth-id", "sub-auth-id", "sub-auth-user"]);
export const dnsNameserversSchema = z
  .object({
    mode: z.enum(["cluster", "per_server", "cloudns"]).default("cluster"),
    nameservers: z
      .array(z.object({ hostname: domainNameSchema, serverId: uuidSchema }))
      .max(6)
      .refine((l) => new Set(l.map((n) => n.hostname)).size === l.length, "Each nameserver hostname can be listed once")
      .default([]),
    /** serverId → that server's nameserver hostnames. */
    perServer: z.record(uuidSchema, z.array(domainNameSchema).max(4)).default({}),
    cloudns: z
      .object({
        authType: cloudnsAuthType.default("auth-id"),
        authId: z.string().trim().min(1).max(100),
        /** Omit to keep the saved password. */
        password: z.string().min(1).max(200).optional(),
        nameservers: z.array(domainNameSchema).min(1, "Enter the ClouDNS nameservers your customers should use").max(8),
      })
      .optional(),
  })
  .refine((v) => v.mode !== "cloudns" || !!v.cloudns, { message: "Enter the ClouDNS API details", path: ["cloudns"] });
export type DnsNameserversInput = z.input<typeof dnsNameserversSchema>;
export const cloudnsTestSchema = z.object({ authType: cloudnsAuthType.default("auth-id"), authId: z.string().trim().min(1).max(100), password: z.string().min(1).max(200).optional() });
export type CloudnsTestInput = z.input<typeof cloudnsTestSchema>;
export const dnsCredentialSchema = z.object({ credential: z.string().trim().min(1).max(512) });
export const dnsRecordSchema = z
  .object({
    type: dnsRecordType,
    name: dnsName,
    content: z.string().trim().min(1).max(2048),
    ttl: dnsTtl,
    priority: z.number().int().min(0).max(65535).optional(),
    proxied: z.boolean().optional(),
  })
  .superRefine((r, ctx) => {
    if ((r.type === "MX" || r.type === "SRV") && r.priority === undefined) ctx.addIssue({ code: "custom", message: `${r.type} records need a priority`, path: ["priority"] });
    if (r.proxied && !["A", "AAAA", "CNAME"].includes(r.type)) ctx.addIssue({ code: "custom", message: "Only A, AAAA and CNAME records can be proxied", path: ["proxied"] });
  });
export type ConnectDnsZoneInput = z.input<typeof connectDnsZoneSchema>;
export type DnsRecordInput = z.input<typeof dnsRecordSchema>;
export type DnsCredentialInput = z.input<typeof dnsCredentialSchema>;

// ---------- Backups ----------
export const backupDestinationSchema = z.object({
  name: z.string().trim().min(1).max(80),
  kind: z.literal("s3").default("s3"),
  endpoint: z.string().trim().min(1).max(255).regex(/^[a-z0-9.-]+(:\d{1,5})?$/i, "Host or host:port, without https://"),
  region: z.string().trim().min(1).max(64).default("us-east-1"),
  bucket: z.string().trim().min(1).max(255).regex(/^[a-z0-9][a-z0-9.-]{1,254}$/, "Enter a valid bucket name"),
  prefix: z.string().trim().max(255).default(""),
  forcePathStyle: z.boolean().default(true),
  /** Pin the destination's TLS certificate by SHA-256 fingerprint (for a self-hosted store with a private CA). Empty verifies a public certificate. */
  tlsPin: z.string().trim().max(200).optional(),
  accessKeyId: z.string().trim().min(1).max(256),
  secretAccessKey: z.string().trim().min(1).max(256),
  isDefault: z.boolean().default(false),
});
export const updateBackupDestinationSchema = backupDestinationSchema.partial().extend({
  // Credentials are only changed when both are supplied; otherwise the stored ones are kept.
  accessKeyId: z.string().trim().min(1).max(256).optional(),
  secretAccessKey: z.string().trim().min(1).max(256).optional(),
  status: z.enum(["ACTIVE", "DISABLED"]).optional(),
});
export const createBackupSchema = z.object({ websiteId: uuidSchema });
export type BackupDestinationInput = z.input<typeof backupDestinationSchema>;
export type UpdateBackupDestinationInput = z.input<typeof updateBackupDestinationSchema>;

// ---------- Security Center ----------
/** An IPv4/IPv6 address or CIDR. Kept strict so only valid values reach the proxy. */
const ipOrCidr = z.string().trim().refine((v) => {
  const [addr, prefix] = v.split("/");
  const v4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  const v6 = /^[0-9a-fA-F:]+$/;
  const okAddr = (v4.test(addr!) && addr!.split(".").every((o) => Number(o) <= 255)) || (v6.test(addr!) && addr!.includes(":"));
  if (!okAddr) return false;
  if (prefix === undefined) return true;
  const max = addr!.includes(":") ? 128 : 32;
  const n = Number(prefix);
  return /^\d+$/.test(prefix) && n >= 0 && n <= max;
}, "Enter a valid IP address or CIDR range (e.g. 203.0.113.4 or 198.51.100.0/24)");
export const websiteSecuritySchema = z
  .object({ ipMode: z.enum(["off", "allow", "deny"]), ipRules: z.array(ipOrCidr).max(200).default([]) })
  .refine((v) => v.ipMode === "off" || v.ipRules.length > 0, { message: "Add at least one IP address or range", path: ["ipRules"] })
  .transform((v) => ({ ipMode: v.ipMode, ipRules: v.ipMode === "off" ? [] : [...new Set(v.ipRules)] }));
export type WebsiteSecurityInput = z.input<typeof websiteSecuritySchema>;
